import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    Subsets,
    subsetSetter,
    subsetGetter,
    subsetUpdateSetter,
    SubsetGetterProps,
    subsetTotalGetter,
    SubsetTotalGetterProps,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";

export type Plats = {
    id: string;
    namn: string;
    underlag: string;
    underlag_display?: { id: string; namn: string };
};

type State = BaseState & {
    platser: Map<Plats["id"], Plats>;
};

const getDefaultState: () => State = () => ({
    platser: new Map(),
    ...cloneDeep(baseState),
});

const moduleName = "platser";
const baseUrl = "skreg/platser/";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        platser: (state: State) => [...state.platser.values()],
        platsById:
            (state: State) =>
            ({ id }: { id?: string }) => {
                if (!id) return null;
                return state.platser.get(id);
            },
        platsSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({ ...props, state, collectionName: "platser" }),
        // Antalet rader som finns för subsetet, det värde paginatorn räknar på
        platsSubsetTotal: (state: State) => (props: SubsetTotalGetterProps<State>) =>
            subsetTotalGetter<State>({ ...props, state }),
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addPlatser: (
            state: State,
            {
                offset,
                subsetName = "default",
                platser = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                platser: Plats[];
            }
        ) =>
            subsetSetter<State, Plats>({
                offset,
                subsetName,
                state,
                collectionName: "platser",
                items: platser,
                includeDefaultSubset: true,
            }),
        updatePlats: (
            state: State,
            {
                plats,
                offset,
            }: {
                plats: Plats[];
                offset: number;
            }
        ) =>
            subsetUpdateSetter<State, Plats>({
                offset,
                state,
                collectionName: "platser",
                items: plats,
                includeDefaultSubset: true,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        fetchPlatser: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams = {},
                forceRefresh = false,
            }: {
                limit?: number;
                page?: number;
                filterParams?: FilterParams;
                forceRefresh?: boolean;
            } = {}
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Plats>({
                    filterParams,
                    state,
                    page,
                    limit,
                    forceRefresh,
                    collectionName: "platser",
                    apiUrl: baseUrl,
                });

                if (results) {
                    commit("addPlatser", { subsetName, offset, platser: results });
                }
                if (typeof total === "number") {
                    commit("setTotal", { total, subsetName });
                }

                return { subsetName, total, nextPage };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        createPlats: async ({ commit, state }: BaseActionsProps<State>, { plats }: { plats: Partial<Plats> }) => {
            try {
                const response = await klinikenApi.post(baseUrl, plats, getConfig({}));

                commit("addPlatser", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    platser: [response.data],
                });
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        updatePlats: async ({ commit, state }: BaseActionsProps<State>, { plats }: { plats: Plats }) => {
            try {
                const response = await klinikenApi.put(`${baseUrl}${plats.id}/`, plats, getConfig({}));

                commit("updatePlats", {
                    plats: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    },
};
